<script lang="ts" setup>
import { ref } from 'vue';
import { Auth, AuthError, ILoginData } from 'Auth';
import { useBrowserBackButtonEvent } from '@/composables/useBrowserBackButtonEvent';
import { BaseButton } from 'Components/Base/BaseButton';
import { BaseNotificationInline } from 'Components/Base/BaseNotificationInline';

const emit = defineEmits<{ (e: 'success', loginData: ILoginData): void }>();
const props = defineProps<{
  signup?: boolean;
  resetError?: true;
  keepLoadingOnSuccess?: boolean;
}>();

const socialLoading = ref('');
const loginError = ref('');

const {
  setBrowserBackButtonListener,
  removeBrowserBackButtonListener,
} = useBrowserBackButtonEvent(() => {
  socialLoading.value = '';
});

async function onSocialClick(socialName: string) {
  socialLoading.value = socialName;
  try {
    setBrowserBackButtonListener();
    const loginData = await Auth.socialLogin(socialName);
    emit('success', loginData);
    if (!props.keepLoadingOnSuccess) socialLoading.value = '';
  } catch (error) {
    socialLoading.value = '';
    loginError.value = (error as AuthError).message;
  } finally {
    removeBrowserBackButtonListener();
  }
}
</script>

<template>
  <div class="AuthSocialButtons auth-social-buttons">
    <!-- eslint-disable vue/no-v-html -->
    <BaseButton
      class="auth-social-buttons__button is--google"
      :loading="socialLoading === 'google-oauth2'"
      tabindex="-1"
      color="primary-dark"
      large
      @click.prevent="onSocialClick('google-oauth2')"
    >
      <span
        alt="google-icon"
        class="svg-icon auth-social-buttons__button-icon"
        v-html="require(`!!raw-loader!images/svg-icons/google-social-icon.svg`).default"
      /> Log In with Google
    </BaseButton>
    <BaseButton
      class="auth-social-buttons__button is--linkedin"
      :loading="socialLoading === 'linkedin'"
      tabindex="-1"
      color="primary-dark"
      large
      @click.prevent="onSocialClick('linkedin')"
    >
      <span
        alt="linkedin-icon"
        class="svg-icon auth-social-buttons__button-icon linkedin-icon"
        v-html="require(`!!raw-loader!images/svg-icons/brands-linkedin-in.svg`).default"
      /> Log In with LinkedIn
    </BaseButton>
    <BaseButton
      class="auth-social-buttons__button is--facebook"
      :loading="socialLoading === 'facebook'"
      tabindex="-1"
      color="primary-dark"
      large
      @click.prevent="onSocialClick('facebook')"
    >
      <span
        alt="facebook-icon"
        class="svg-icon auth-social-buttons__button-icon"
        v-html="require(`!!raw-loader!images/svg-icons/brands-facebook-f.svg`).default"
      /> Log In with Facebook
    </BaseButton>
    <BaseNotificationInline
      :show="Boolean(loginError)"
      class="auth-social-buttons__notification"
    >
      <!-- eslint-disable-next-line vue/no-v-html -->
      <span v-html="loginError" />
    </BaseNotificationInline>
  </div>
</template>

<style lang="sass" scoped>
@import '~Styles/_variables'
.auth-social-buttons
  display: grid
  gap: 12px
  width: 100%
  &__button
  &__button-icon.svg-icon
    margin-right: 12px
    :deep(svg)
      height: 100%
  .linkedin-icon
    :deep(svg)
      height: 18px
  & .is--facebook
    fill: #3B5998 !important
  & .is--linkedin
    fill: #0077B5 !important
</style>
